/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { custApi, formatErr } from "../../lib/customerApi";
import { payHealthBooking, PayMethodPicker } from "../../components/payments/HealthPay";
import AlarmSoundPicker from "./AlarmSoundPicker";
import AddressPicker from "../../components/AddressPicker";
import LovedOnePicker from "../../components/LovedOnePicker";
import AmbulanceMapView from "../../components/AmbulanceMapView";
import {
  Stethoscope, TestTube, ArrowLeft, Calendar, Clock, MapPin, Phone,
  ArrowRight, ShieldCheck, Hourglass, CheckCircle, XCircle,
  ArrowsClockwise, MagnifyingGlass, House, Star, CurrencyInr,
  Ambulance as AmbulanceIcon, Lightning, FileText, Download,
  Syringe, Pill as PillIcon, Users, Plus, Trash, Bell, BellRinging,
  Heart, Baby, FirstAid, Moon, ChartLineUp, PawPrint, Package, BookOpen, GraduationCap,
} from "@phosphor-icons/react";

const STATUS_META = {
  requested:   { Icon: Hourglass,        color: "#C9A227", lbl: "Awaiting" },
  confirmed:   { Icon: CheckCircle,      color: "#0B2B66", lbl: "Confirmed" },
  in_progress: { Icon: ArrowsClockwise,  color: "#C25D38", lbl: "In progress" },
  completed:   { Icon: ShieldCheck,      color: "#DC2626", lbl: "Completed" },
  cancelled:   { Icon: XCircle,          color: "#8A8780", lbl: "Cancelled" },
  no_show:     { Icon: XCircle,          color: "#7A2E18", lbl: "No-show" },
};

// =====================================================================
// Hub: /me/health  — three primary tiles
// =====================================================================
export function MeHealthHub() {
  return (
    <div className="font-body px-5 py-5" data-testid="me-health-hub">
      <div className="mb-5">
        <div className="text-[10px] uppercase tracking-[0.22em] text-gray-400 font-bold">Rescigo · Health</div>
        <h1 className="font-display font-black text-[26px] leading-tight tracking-tight text-[#111827] mt-1">
          Doctors, labs &amp; ambulances <span className="text-[#DC2626]">on demand.</span>
        </h1>
        <p className="text-[13px] text-gray-500 mt-1.5 leading-relaxed max-w-xl">
          Book teleconsult, schedule a home lab visit or call an ambulance —
          all from your unified Rescigo account.
        </p>
      </div>

      <section className="grid grid-cols-1 gap-3">
        <Tile to="/me/health/doctors" Icon={Stethoscope} color="#0B2B66"
              title="Doctor consultation" blurb="Teleconsult · clinic · home visits"/>
        <Tile to="/me/health/labs" Icon={TestTube} color="#DC2626"
              title="Lab tests" blurb="Home collection · digital reports"/>
        <Tile to="/me/health/ambulance" Icon={AmbulanceIcon} color="#7A2E18"
              title="Ambulance" blurb="Emergency · scheduled · ICU transport" red/>
      </section>

      {/* Iter 154 — Care reminders for family */}
      <div className="mt-6 mb-2">
        <div className="text-[10px] uppercase tracking-[0.22em] text-gray-400 font-bold">
          Care Reminders
        </div>
        <div className="text-[12px] text-gray-500 mt-0.5">
          Vaccines and daily pills — set once, never miss.
        </div>
      </div>
      <section className="grid grid-cols-2 gap-3" data-testid="me-health-reminders">
        <Tile to="/me/health/vaccines" Icon={Syringe} color="#0B2B66"
              title="Vaccines" blurb="Baby & family schedule"/>
        <Tile to="/me/health/pills" Icon={PillIcon} color="#DC2626"
              title="Pill reminders" blurb="Daily medicines"/>
      </section>

      {/* Iter 155 Phase 1 — Daily care trackers */}
      <div className="mt-6 mb-2">
        <div className="text-[10px] uppercase tracking-[0.22em] text-gray-400 font-bold">
          Daily Care
        </div>
        <div className="text-[12px] text-gray-500 mt-0.5">
          Log a vital, feed the baby, or call for help — in one tap.
        </div>
      </div>
      <section className="grid grid-cols-2 gap-3" data-testid="me-daily-care">
        <Tile to="/me/health/vitals" Icon={Heart} color="#DC2626"
              title="Vitals" blurb="BP · sugar · SpO2"/>
        <Tile to="/me/health/cycle" Icon={Moon} color="#EC4899"
              title="Cycle" blurb="Period · fertility · PMS"/>
        <Tile to="/me/health/pregnancy" Icon={Baby} color="#BE185D"
              title="Pregnancy" blurb="Week-by-week · kicks"/>
        <Tile to="/me/health/growth" Icon={ChartLineUp} color="#2563EB"
              title="Baby growth" blurb="WHO chart · vaccines"/>
        <Tile to="/me/health/elder" Icon={Heart} color="#DC2626"
              title="Elder Care" blurb="Vitals · meds · SOS"/>
        <Tile to="/me/health/kids" Icon={GraduationCap} color="#7C3AED"
              title="Kids Care" blurb="Age 5-18 · habits · growth"/>
        <Tile to="/me/health/baby" Icon={Baby} color="#2563EB"
              title="Baby daily" blurb="Feed · sleep · diaper"/>
        <Tile to="/me/health/pet" Icon={PawPrint} color="#0F766E"
              title="Pet care" blurb="Vaccines · insurance"/>
        <Tile to="/me/health/inventory" Icon={Package} color="#B45309"
              title="Inventory" blurb="Stock alerts"/>
        <Tile to="/me/health/circle" Icon={BookOpen} color="#7C3AED"
              title="Care Circle" blurb="Tips & guides"/>
        <Tile to="/me/health/emergency" Icon={FirstAid} color="#7F1D1D"
              title="SOS" blurb="One-touch call" red/>
      </section>

      <Link to="/me/health/bookings" data-testid="link-my-bookings"
             className="inline-flex items-center gap-1.5 text-xs text-[#0B2B66] hover:underline mt-2">
        <Calendar size={12}/> My health bookings <ArrowRight size={11}/>
      </Link>
    </div>
  );
}

function Tile({ to, Icon, color, title, blurb, red = false }) {
  return (
    <Link to={to} className="bg-white border border-[#E5E2D9] rounded-xl p-5 hover:shadow-sm hover:border-[#DC2626] transition-all relative overflow-hidden">
      {red && <span className="absolute top-2 right-2 text-[9px] uppercase tracking-[0.15em] text-[#7A2E18] bg-[#FCEEEA] px-1.5 py-0.5 rounded">SOS</span>}
      <Icon size={24} weight="duotone" color={color}/>
      <div className="font-heading text-lg mt-2.5" style={{ color }}>{title}</div>
      <div className="text-xs text-[#5C5A55] mt-1">{blurb}</div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-[#8A8780] mt-3 inline-flex items-center gap-1">
        Open <ArrowRight size={9}/>
      </div>
    </Link>
  );
}

// =====================================================================
// Doctor list /me/health/doctors
// =====================================================================
export function DoctorList() {
  const [doctors, setDoctors] = useState([]);
  const [q, setQ] = useState("");
  const [mode, setMode] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const r = await custApi.get("/me/health/doctors", { params: { q: q || undefined, mode: mode || undefined } });
      setDoctors(r.data.doctors || []);
    } catch (e) { toast.error(formatErr(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, [mode]);

  return (
    <div className="space-y-4 px-4 sm:px-6 py-5" data-testid="doctor-list">
      <BackHeader to="/me/health" title="Doctor consultation"/>
      <div className="flex gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlass size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#8A8780]"/>
          <input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === "Enter" && load()}
                  placeholder="Search by name or specialty"
                  className="h-9 w-full pl-7 pr-3 border border-[#E5E2D9] rounded text-sm"/>
        </div>
        <select value={mode} onChange={e => setMode(e.target.value)}
                 className="h-9 border border-[#E5E2D9] rounded px-2 text-sm">
          <option value="">All modes</option>
          <option value="teleconsult">Teleconsult</option>
          <option value="clinic">Clinic visit</option>
          <option value="home">Home visit</option>
        </select>
      </div>

      {loading && <Skeleton/>}
      {!loading && doctors.length === 0 && (
        <div className="bg-white border border-dashed border-[#E5E2D9] rounded-xl p-10 text-center">
          <Stethoscope size={28} color="#8A8780"/>
          <div className="font-heading text-lg mt-2">No doctors yet</div>
          <p className="text-xs text-[#8A8780] mt-1">Check back soon — partners are onboarding.</p>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-3">
        {doctors.map(d => <DoctorCard key={d.id} doctor={d}/>)}
      </div>
    </div>
  );
}

function DoctorCard({ doctor }) {
  const p = doctor.profile || {};
  const cheapestFee = ["fee_teleconsult", "fee_clinic", "fee_home"]
    .map(k => p[k]).filter(v => v != null).sort((a, b) => a - b)[0];
  return (
    <Link to={`/me/health/doctors/${doctor.id}`} data-testid={`doctor-card-${doctor.id}`} className="bg-white rounded-[20px] border border-[#F1EFEC] shadow-[0_6px_24px_rgba(17,24,39,0.05)] p-4 block active:scale-[0.99] transition-transform">
      <div className="flex items-start gap-3">
        <div className="w-14 h-14 rounded-2xl bg-[#FFF1F1] grid place-items-center text-[#DC2626] flex-none">
          <Stethoscope size={26} weight="duotone"/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start gap-2">
            <div className="flex-1 min-w-0 font-display font-bold text-[16px] text-[#111827] truncate">{doctor.name}</div>
            <span className="shrink-0 text-[13px] font-semibold text-[#DC2626] bg-[#FFF1F1] rounded-lg px-3 py-1">View →</span>
          </div>
          <div className="text-[13px] text-[#DC2626] font-medium">{p.specialty}</div>
          <div className="text-[12px] text-[#6B7280] mt-0.5 truncate">{p.qualifications || "—"}</div>
          <div className="flex items-center gap-3 mt-2 text-[12px] text-[#374151]">
            {p.years_experience ? <span className="inline-flex items-center gap-1"><Star size={12} weight="duotone"/> {p.years_experience} yrs</span> : null}
            {cheapestFee != null && <span className="inline-flex items-center gap-1"><CurrencyInr size={12}/> from ₹{cheapestFee}</span>}
          </div>
          <div className="flex gap-1 mt-2">
            {p.fee_teleconsult != null && <Pill v="Tele" color="#0B2B66"/>}
            {p.fee_clinic != null && <Pill v="Clinic" color="#DC2626"/>}
            {p.fee_home != null && <Pill v="Home" color="#C25D38"/>}
          </div>
        </div>
      </div>
    </Link>
  );
}

function Pill({ v, color }) {
  return (
    <span className="text-[9px] uppercase tracking-[0.15em] px-1.5 py-0.5 rounded"
           style={{ color, border: `1px solid ${color}33`, background: `${color}0D` }}>
      {v}
    </span>
  );
}

// =====================================================================
// Doctor detail /me/health/doctors/:pid → pick mode + slot
// =====================================================================
export function DoctorDetail() {
  const { pid } = useParams();
  const nav = useNavigate();
  const [doctor, setDoctor] = useState(null);
  const [slotsData, setSlotsData] = useState({ days: [] });
  const [mode, setMode] = useState("");
  const [selSlot, setSelSlot] = useState(null);
  const [form, setForm] = useState({ chief_complaint: "", patient_name: "", patient_age: "", patient_gender: "", phone: "", address: "", latitude: null, longitude: null, loved_one_id: null });
  const [busy, setBusy] = useState(false);
  const [payMethod, setPayMethod] = useState("wallet");

  useEffect(() => {
    (async () => {
      try {
        const r = await custApi.get("/me/health/doctors");
        const d = (r.data.doctors || []).find(x => x.id === pid);
        if (!d) { toast.error("Doctor not found"); nav("/me/health/doctors"); return; }
        setDoctor(d);
        // Auto-pick the first available mode
        const modes = ["teleconsult", "clinic", "home"].filter(m => d.profile[`fee_${m}`] != null);
        if (modes[0]) setMode(modes[0]);
      } catch (e) { toast.error(formatErr(e)); }
    })();
  }, [pid]);

  useEffect(() => {
    if (!doctor || !mode) return;
    custApi.get(`/me/health/doctors/${pid}/slots`, { params: { days: 7, mode } })
      .then(r => setSlotsData(r.data))
      .catch(e => toast.error(formatErr(e)));
  }, [doctor, mode]);

  if (!doctor) return <Skeleton/>;
  const p = doctor.profile;
  const fee = p[`fee_${mode}`];

  const book = async () => {
    if (!selSlot) { toast.error("Pick a slot first"); return; }
    setBusy(true);
    try {
      const r = await custApi.post("/me/health/doctors/book", {
        partner_id: pid, mode,
        slot_date: selSlot.date, slot_time: selSlot.time,
        chief_complaint: form.chief_complaint || null,
        patient_name: form.patient_name || null,
        patient_age: form.patient_age ? Number(form.patient_age) : null,
        patient_gender: form.patient_gender || null,
        phone: form.phone || null,
        address: form.address || null,
      });
      toast.success(`Booked ${r.data.no}`);
      if (payMethod === "wallet" || payMethod === "razorpay") await payHealthBooking(r.data, payMethod);
      nav("/me/health/bookings");
    } catch (e) { toast.error(formatErr(e)); }
    setBusy(false);
  };

  return (
    <div className="space-y-4 px-4 sm:px-6 py-5" data-testid="doctor-detail">
      <BackHeader to="/me/health/doctors" title={doctor.name}/>
      <div className="bg-white rounded-xl border border-[#E5E2D9] p-5">
        <div className="flex items-start gap-4">
          <div className="w-16 h-16 rounded-full bg-[#F0ECE3] grid place-items-center text-[#DC2626] flex-none">
            <Stethoscope size={26} weight="duotone"/>
          </div>
          <div className="flex-1">
            <div className="font-heading text-2xl">{doctor.name}</div>
            <div className="text-sm text-[#DC2626]">{p.specialty}</div>
            <div className="text-xs text-[#5C5A55] mt-0.5">{p.qualifications || "—"} {p.years_experience ? `· ${p.years_experience} yrs exp` : ""}</div>
            {p.bio && <p className="text-xs text-[#5C5A55] mt-2 max-w-prose">{p.bio}</p>}
          </div>
        </div>
        <div className="mt-4 grid grid-cols-3 gap-2">
          {["teleconsult", "clinic", "home"].map(m => (
            p[`fee_${m}`] != null && (
              <button key={m} onClick={() => { setMode(m); setSelSlot(null); }} data-testid={`mode-${m}`}
                       className={`p-3 rounded-lg border text-left ${mode === m ? "border-[#DC2626] bg-[#F4F8F2]" : "border-[#E5E2D9]"}`}>
                <div className="text-[10px] uppercase tracking-[0.15em] text-[#8A8780]">{m === "teleconsult" ? "Teleconsult" : m === "clinic" ? "Clinic visit" : "Home visit"}</div>
                <div className="font-heading text-lg mt-0.5">₹{p[`fee_${m}`]}</div>
              </button>
            )
          ))}
        </div>
      </div>

      {/* Slots */}
      <div className="bg-white rounded-xl border border-[#E5E2D9] p-5">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#8A8780]">Available slots · Next 7 days</div>
        {slotsData.days.length === 0 && (
          <div className="mt-3 text-sm text-[#8A8780]">No slots configured for this mode.</div>
        )}
        <div className="mt-3 space-y-3">
          {slotsData.days.map(day => (
            <div key={day.date}>
              <div className="text-xs font-medium text-[#1A1A1A] mb-1.5">{prettyDate(day.date)}</div>
              <div className="flex flex-wrap gap-1.5">
                {day.slots.map(s => (
                  <button key={s.time} disabled={!s.available}
                           onClick={() => setSelSlot({ date: day.date, time: s.time })}
                           data-testid={`slot-${day.date}-${s.time}`}
                           className={`h-8 px-2.5 rounded text-[11px] border ${
                             !s.available ? "border-[#F0ECE3] text-[#C0BDB5] cursor-not-allowed line-through" :
                             selSlot?.date === day.date && selSlot?.time === s.time ? "bg-[#DC2626] text-white border-[#DC2626]" :
                             "bg-white border-[#E5E2D9] text-[#1A1A1A] hover:border-[#DC2626]"
                           }`}>
                    {s.time}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Form */}
      <div className="bg-white rounded-xl border border-[#E5E2D9] p-4 sm:p-5">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#8A8780] mb-3">Patient & visit info</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <LovedOnePicker
            selectedId={form.loved_one_id}
            testidPrefix="doc-lop"
            onSelect={(pr) => setForm(f => ({
              ...f,
              loved_one_id: pr.loved_one_id,
              patient_name: pr.name || f.patient_name,
              patient_age: pr.age ? String(pr.age) : f.patient_age,
              patient_gender: pr.gender || f.patient_gender,
              phone: pr.phone || f.phone,
            }))}
          />
          <Field label="Patient name" v={form.patient_name} onChange={v => setForm({ ...form, patient_name: v })}/>
          <Field label="Age" v={form.patient_age} onChange={v => setForm({ ...form, patient_age: v })} type="number"/>
          <div>
            <label className="text-[10px] uppercase tracking-[0.15em] text-[#8A8780]">Gender</label>
            <select value={form.patient_gender} onChange={e => setForm({ ...form, patient_gender: e.target.value })}
                     className="mt-1 h-9 w-full border border-[#E5E2D9] rounded px-2 text-sm">
              <option value="">—</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
            </select>
          </div>
          <Field label="Contact phone" v={form.phone} onChange={v => setForm({ ...form, phone: v })}/>
          <Field label="Chief complaint" v={form.chief_complaint} onChange={v => setForm({ ...form, chief_complaint: v })} wide multiline/>
          {mode === "home" && (
            <AddressPicker
              value={{ address: form.address, latitude: form.latitude, longitude: form.longitude }}
              onChange={(patch) => setForm({ ...form, ...patch })}
              partnerId={pid}
              orderAmount={fee || 0}
              label="Home visit address"
              testidPrefix="doc-addr"
            />
          )}
        </div>
        {fee > 0 && <PayMethodPicker value={payMethod} onChange={setPayMethod} testPrefix="doc"/>}
        <button onClick={book} disabled={busy || !selSlot} data-testid="book-doctor"
                 className="mt-4 h-10 px-4 bg-[#DC2626] hover:bg-[#B91C1C] disabled:opacity-50 text-white rounded text-sm font-medium inline-flex items-center gap-1.5">
          Confirm booking · ₹{fee} <ArrowRight size={12}/>
        </button>
      </div>
    </div>
  );
}

// =====================================================================
// Lab list /me/health/labs
// =====================================================================
export function LabList() {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    custApi.get("/me/health/lab-partners")
      .then(r => { setPartners(r.data.partners || []); setLoading(false); })
      .catch(e => { toast.error(formatErr(e)); setLoading(false); });
  }, []);
  return (
    <div className="space-y-4 px-4 sm:px-6 py-5" data-testid="lab-list">
      <BackHeader to="/me/health" title="Lab tests"/>
      {loading && <Skeleton/>}
      <div className="grid md:grid-cols-2 gap-3">
        {partners.map(p => (
          <Link key={p.id} to={`/me/health/labs/${p.id}`} data-testid={`lab-card-${p.id}`}
                 className="bg-white rounded-[20px] border border-[#F1EFEC] shadow-[0_6px_24px_rgba(17,24,39,0.05)] p-4 block active:scale-[0.99] transition-transform">
            <div className="flex items-start gap-3">
              <div className="w-14 h-14 rounded-2xl bg-[#ECFDF3] grid place-items-center text-[#16A34A] flex-none"><TestTube size={26} weight="duotone"/></div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start gap-2">
                  <div className="flex-1 min-w-0 font-display font-bold text-[16px] text-[#111827] truncate">{p.name}</div>
                  <span className="shrink-0 text-[13px] font-semibold text-[#DC2626] bg-[#FFF1F1] rounded-lg px-3 py-1">View →</span>
                </div>
                <div className="text-[13px] text-[#6B7280] flex items-center gap-1 mt-0.5"><MapPin size={13} className="shrink-0"/> <span className="truncate">{p.address || "—"}</span></div>
                <div className="text-[13px] text-[#15803D] font-medium mt-1">{p.tests_count} tests offered</div>
              </div>
            </div>
          </Link>
        ))}
        {!loading && !partners.length && (
          <div className="md:col-span-2 bg-white border border-dashed border-[#E5E2D9] rounded-xl p-10 text-center text-[#8A8780] text-sm">
            No labs onboarded yet.
          </div>
        )}
      </div>
    </div>
  );
}

// =====================================================================
// Lab detail — pick tests + slot + book
// =====================================================================
export function LabDetail() {
  const { pid } = useParams();
  const nav = useNavigate();
  const [partner, setPartner] = useState(null);
  const [tests, setTests] = useState([]);
  const [sel, setSel] = useState(new Set());
  const [visitType, setVisitType] = useState("home");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [time, setTime] = useState("09:00");
  const [form, setForm] = useState({ patient_name: "", patient_age: "", patient_gender: "", phone: "", address: "", latitude: null, longitude: null, loved_one_id: null });
  const [busy, setBusy] = useState(false);
  const [payMethod, setPayMethod] = useState("wallet");

  useEffect(() => {
    (async () => {
      try {
        const [pr, tr] = await Promise.all([
          custApi.get(`/me/partners/${pid}`),
          custApi.get(`/me/health/lab-partners/${pid}/tests`),
        ]);
        setPartner(pr.data);
        setTests(tr.data.tests || []);
      } catch (e) { toast.error(formatErr(e)); nav("/me/health/labs"); }
    })();
  }, [pid]);

  const selectedTests = tests.filter(t => sel.has(t.id));
  const testTotal = selectedTests.reduce((a, t) => a + Number(t.price || 0), 0);
  const homeFee = visitType === "home"
    ? Math.max(0, ...selectedTests.map(t => Number(t.home_collection_fee || 0)))
    : 0;
  const total = testTotal + homeFee;

  const toggle = (id) => {
    const ns = new Set(sel);
    if (ns.has(id)) ns.delete(id); else ns.add(id);
    setSel(ns);
  };

  const book = async () => {
    if (!sel.size) { toast.error("Pick at least one test"); return; }
    if (visitType === "home" && !form.address) { toast.error("Address required for home visit"); return; }
    setBusy(true);
    try {
      const r = await custApi.post("/me/health/lab/book", {
        partner_id: pid, test_ids: Array.from(sel),
        visit_type: visitType, slot_date: date, slot_time: time,
        patient_name: form.patient_name || null,
        patient_age: form.patient_age ? Number(form.patient_age) : null,
        patient_gender: form.patient_gender || null,
        phone: form.phone || null,
        address: form.address || null,
      });
      toast.success(`Booked ${r.data.no}`);
      if (payMethod === "wallet" || payMethod === "razorpay") await payHealthBooking(r.data, payMethod);
      nav("/me/health/bookings");
    } catch (e) { toast.error(formatErr(e)); }
    setBusy(false);
  };

  if (!partner) return <Skeleton/>;
  return (
    <div className="space-y-4 px-4 sm:px-6 py-5" data-testid="lab-detail">
      <BackHeader to="/me/health/labs" title={partner.name}/>
      <div className="bg-white rounded-xl border border-[#E5E2D9] p-4 sm:p-5">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#8A8780]">Visit type</div>
        <div className="mt-2 grid grid-cols-2 gap-2 max-w-md">
          <button onClick={() => setVisitType("home")} data-testid="visit-home"
                   className={`p-3 rounded-lg border text-left ${visitType === "home" ? "border-[#DC2626] bg-[#F4F8F2]" : "border-[#E5E2D9]"}`}>
            <House size={14} weight="duotone" color="#DC2626"/>
            <div className="text-sm font-medium mt-1">Home collection</div>
            <div className="text-[10px] text-[#8A8780]">Sample collected at your address</div>
          </button>
          <button onClick={() => setVisitType("walk_in")}
                   className={`p-3 rounded-lg border text-left ${visitType === "walk_in" ? "border-[#DC2626] bg-[#F4F8F2]" : "border-[#E5E2D9]"}`}>
            <TestTube size={14} weight="duotone" color="#0B2B66"/>
            <div className="text-sm font-medium mt-1">Walk-in</div>
            <div className="text-[10px] text-[#8A8780]">Visit the lab</div>
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-[#E5E2D9] p-4 sm:p-5">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#8A8780] mb-2">Tests ({tests.length})</div>
        <ul className="divide-y divide-[#F0ECE3]">
          {tests.map(t => (
            <li key={t.id} className="py-2.5 flex items-start gap-3 cursor-pointer"
                 onClick={() => toggle(t.id)} data-testid={`lab-test-${t.id}`}>
              <input type="checkbox" checked={sel.has(t.id)} onChange={() => toggle(t.id)} className="mt-1"/>
              <div className="flex-1">
                <div className="text-sm font-medium">{t.name}</div>
                <div className="text-[11px] text-[#8A8780]">{t.sample_type} · {t.report_tat_hours}h TAT {t.prep_notes ? `· ${t.prep_notes}` : ""}</div>
              </div>
              <div className="text-sm font-mono">₹{Number(t.price).toLocaleString("en-IN")}</div>
            </li>
          ))}
          {!tests.length && <li className="py-6 text-center text-[#8A8780] text-sm">No tests offered yet</li>}
        </ul>
      </div>

      <div className="bg-white rounded-xl border border-[#E5E2D9] p-4 sm:p-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-[0.15em] text-[#8A8780]">Date</label>
            <input type="date" value={date} onChange={e => setDate(e.target.value)}
                    className="mt-1 h-9 w-full border border-[#E5E2D9] rounded px-2 text-sm"/>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.15em] text-[#8A8780]">Time</label>
            <input type="time" value={time} onChange={e => setTime(e.target.value)}
                    className="mt-1 h-9 w-full border border-[#E5E2D9] rounded px-2 text-sm"/>
          </div>
          <LovedOnePicker
            selectedId={form.loved_one_id}
            testidPrefix="lab-lop"
            onSelect={(pr) => setForm(f => ({
              ...f,
              loved_one_id: pr.loved_one_id,
              patient_name: pr.name || f.patient_name,
              patient_age: pr.age ? String(pr.age) : f.patient_age,
              patient_gender: pr.gender || f.patient_gender,
              phone: pr.phone || f.phone,
            }))}
          />
          <Field label="Patient name" v={form.patient_name} onChange={v => setForm({ ...form, patient_name: v })}/>
          <Field label="Age" v={form.patient_age} onChange={v => setForm({ ...form, patient_age: v })} type="number"/>
          <Field label="Contact phone" v={form.phone} onChange={v => setForm({ ...form, phone: v })}/>
          {visitType === "home" && (
            <AddressPicker
              value={{ address: form.address, latitude: form.latitude, longitude: form.longitude }}
              onChange={(patch) => setForm({ ...form, ...patch })}
              partnerId={pid}
              orderAmount={total || 0}
              label="Home collection address"
              testidPrefix="lab-addr"
            />
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-[#E5E2D9] p-4 sm:p-5">
        <div className="flex items-center justify-between text-sm">
          <span className="text-[#5C5A55]">Tests subtotal</span>
          <span className="font-mono">₹{testTotal.toLocaleString("en-IN")}</span>
        </div>
        {visitType === "home" && (
          <div className="flex items-center justify-between text-sm mt-1">
            <span className="text-[#5C5A55]">Home collection fee</span>
            <span className="font-mono">₹{homeFee.toLocaleString("en-IN")}</span>
          </div>
        )}
        <div className="flex items-center justify-between text-base font-heading mt-2 pt-2 border-t border-[#F0ECE3]">
          <span>Total</span>
          <span>₹{total.toLocaleString("en-IN")}</span>
        </div>
        {sel.size > 0 && <PayMethodPicker value={payMethod} onChange={setPayMethod} testPrefix="lab"/>}
        <button onClick={book} disabled={busy || !sel.size} data-testid="book-lab"
                 className="mt-3 h-10 w-full bg-[#DC2626] hover:bg-[#B91C1C] disabled:opacity-50 text-white rounded text-sm font-medium inline-flex items-center justify-center gap-1.5">
          Confirm lab booking <ArrowRight size={12}/>
        </button>
      </div>
    </div>
  );
}

// =====================================================================
// Ambulance list + booking
// =====================================================================
export function AmbulanceList() {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [vehClass, setVehClass] = useState("");
  const [view, setView] = useState("map");   // "map" or "list"

  const load = async () => {
    setLoading(true);
    try {
      const r = await custApi.get("/me/health/ambulance-partners", { params: { vehicle_class: vehClass || undefined } });
      setPartners(r.data.partners || []);
    } catch (e) { toast.error(formatErr(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, [vehClass]);

  return (
    <div className="space-y-4 px-4 sm:px-6 py-5" data-testid="amb-list">
      <BackHeader to="/me/health" title="Ambulance"/>
      <div className="bg-[#FCEEEA] border border-[#F0DAD3] rounded-xl p-4 flex items-start gap-3">
        <Lightning size={20} weight="duotone" color="#7A2E18" className="flex-none mt-0.5"/>
        <div>
          <div className="text-sm font-heading text-[#7A2E18]">Emergency? Tap a partner below</div>
          <p className="text-xs text-[#5C5A55] mt-0.5">Emergency requests auto-confirm; the partner dispatches the nearest matching vehicle.</p>
        </div>
      </div>

      {/* Filters + view toggle */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex gap-2 flex-wrap flex-1">
          {["", "BLS", "ALS", "ICU", "private"].map(c => (
            <button key={c || "all"} onClick={() => setVehClass(c)}
                     className={`h-8 px-3 rounded text-[11px] border ${vehClass === c ? "bg-[#DC2626] text-white border-[#DC2626]" : "bg-white border-[#E5E2D9] text-[#5C5A55]"}`}>
              {c || "All classes"}
            </button>
          ))}
        </div>
        <div className="inline-flex rounded-lg overflow-hidden border border-[#E5E2D9] bg-white shrink-0">
          <button
            data-testid="amb-view-map"
            onClick={() => setView("map")}
            className={`h-8 px-3 text-[11px] font-semibold ${view === "map" ? "bg-[#7A2E18] text-white" : "text-[#5C5A55]"}`}
          >Map</button>
          <button
            data-testid="amb-view-list"
            onClick={() => setView("list")}
            className={`h-8 px-3 text-[11px] font-semibold ${view === "list" ? "bg-[#7A2E18] text-white" : "text-[#5C5A55]"}`}
          >List</button>
        </div>
      </div>

      {loading && <Skeleton/>}

      {!loading && view === "map" && (
        <AmbulanceMapView partners={partners}/>
      )}

      {!loading && view === "list" && (
        <div className="grid md:grid-cols-2 gap-3">
          {partners.map(p => (
            <Link key={p.id} to={`/me/health/ambulance/${p.id}`} data-testid={`amb-card-${p.id}`}
                   className="bg-white rounded-[20px] border border-[#F1EFEC] shadow-[0_6px_24px_rgba(17,24,39,0.05)] p-4 block active:scale-[0.99] transition-transform">
              <div className="flex items-start gap-3">
                <div className="w-14 h-14 rounded-2xl bg-[#FCEEEA] grid place-items-center text-[#7A2E18] flex-none"><AmbulanceIcon size={26} weight="duotone"/></div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start gap-2">
                    <div className="flex-1 min-w-0 font-display font-bold text-[16px] text-[#111827] truncate">{p.name}</div>
                    <span className="shrink-0 text-[13px] font-semibold text-[#DC2626] bg-[#FFF1F1] rounded-lg px-3 py-1">Book →</span>
                  </div>
                  <div className="text-[13px] text-[#6B7280] flex items-center gap-1 mt-0.5"><MapPin size={13} className="shrink-0"/> <span className="truncate">{p.address || "—"}</span></div>
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {(p.vehicle_classes || []).map(c => <Pill key={c} v={c} color="#7A2E18"/>)}
                  </div>
                  <div className="text-[11px] text-[#8A8780] mt-1.5">
                    {p.base_fare != null ? `From ₹${p.base_fare} base` : ""} {p.per_km_rate ? `· ₹${p.per_km_rate}/km` : ""}
                  </div>
                </div>
              </div>
            </Link>
          ))}
          {!partners.length && (
            <div className="md:col-span-2 bg-white border border-dashed border-[#E5E2D9] rounded-xl p-10 text-center text-[#8A8780] text-sm">
              No ambulance providers in range
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function AmbulanceBook() {
  const { pid } = useParams();
  const nav = useNavigate();
  const [partner, setPartner] = useState(null);
  const [reqType, setReqType] = useState("emergency");
  const [vehClass, setVehClass] = useState("BLS");
  const [form, setForm] = useState({
    pickup_address: "", drop_address: "", scheduled_at: "",
    patient_name: "", patient_age: "", contact_phone: "", notes: "",
    pickup_latitude: null, pickup_longitude: null,
    drop_latitude: null, drop_longitude: null,
    loved_one_id: null,
  });
  const [busy, setBusy] = useState(false);
  const [quote, setQuote] = useState(null);
  const [quoteBusy, setQuoteBusy] = useState(false);
  const [payMethod, setPayMethod] = useState("wallet");

  useEffect(() => {
    custApi.get(`/me/partners/${pid}`).then(r => {
      setPartner(r.data);
    }).catch(e => { toast.error(formatErr(e)); nav("/me/health/ambulance"); });
  }, [pid]);

  // Live fare quote — recompute when pickup / drop / class change.
  useEffect(() => {
    if (form.pickup_latitude == null || form.pickup_longitude == null) {
      setQuote(null);
      return;
    }
    setQuoteBusy(true);
    const t = setTimeout(() => {
      custApi.post("/me/health/ambulance/quote", {
        partner_id: pid,
        vehicle_class: vehClass,
        pickup_lat: form.pickup_latitude,
        pickup_lng: form.pickup_longitude,
        drop_lat: form.drop_latitude,
        drop_lng: form.drop_longitude,
      }).then(r => setQuote(r.data))
        .catch(() => setQuote(null))
        .finally(() => setQuoteBusy(false));
    }, 350);
    return () => clearTimeout(t);
  }, [pid, vehClass, form.pickup_latitude, form.pickup_longitude, form.drop_latitude, form.drop_longitude]);

  const book = async () => {
    if (!form.pickup_address) { toast.error("Pickup address is required"); return; }
    if (reqType === "scheduled" && !form.scheduled_at) { toast.error("Scheduled time required"); return; }
    setBusy(true);
    try {
      const r = await custApi.post("/me/health/ambulance/book", {
        partner_id: pid, request_type: reqType, vehicle_class: vehClass,
        pickup_address: form.pickup_address,
        pickup_lat: form.pickup_latitude,
        pickup_lng: form.pickup_longitude,
        drop_address: form.drop_address || null,
        drop_lat: form.drop_latitude,
        drop_lng: form.drop_longitude,
        scheduled_at: reqType === "scheduled" ? new Date(form.scheduled_at).toISOString() : null,
        patient_name: form.patient_name || null,
        patient_age: form.patient_age ? Number(form.patient_age) : null,
        contact_phone: form.contact_phone || null,
        notes: form.notes || null,
        payment_method: payMethod,
        fare_total: quote?.total || null,
      });
      toast.success(`Booked ${r.data.no}`);
      if (payMethod === "wallet" || payMethod === "razorpay") await payHealthBooking(r.data, payMethod);
      nav("/me/health/bookings");
    } catch (e) { toast.error(formatErr(e)); }
    setBusy(false);
  };

  if (!partner) return <Skeleton/>;
  return (
    <div className="space-y-4 px-4 sm:px-6 py-5" data-testid="amb-book">
      <BackHeader to="/me/health/ambulance" title={partner.name}/>

      {/* Vehicle class + equipment badges */}
      {(partner.vehicle_class || (partner.equipment_list && partner.equipment_list.length > 0)) && (
        <div className="bg-white rounded-xl border border-[#E5E2D9] p-4 sm:p-5" data-testid="amb-vehicle-badge">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-md bg-[#7A2E18] text-white flex items-center justify-center shrink-0">
              <AmbulanceIcon size={20} weight="fill"/>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#8A8780] font-semibold">
                Vehicle class
              </div>
              <div className="font-heading text-lg text-[#1A1A1A] leading-tight mt-0.5">
                {partner.vehicle_class || "Standard"}
              </div>
              {partner.equipment_list && partner.equipment_list.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {partner.equipment_list.map((e, i) => (
                    <span key={i}
                           className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-[#FEE2E2] text-[#7F1D1D]">
                      {e}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-[#E5E2D9] p-4 sm:p-5">
        <div className="grid grid-cols-2 gap-2 max-w-md">
          <button onClick={() => setReqType("emergency")} data-testid="req-emergency"
                   className={`p-3 rounded-lg border text-left ${reqType === "emergency" ? "border-[#7A2E18] bg-[#FCEEEA]" : "border-[#E5E2D9]"}`}>
            <Lightning size={14} weight="duotone" color="#7A2E18"/>
            <div className="text-sm font-medium mt-1">Emergency · SOS</div>
            <div className="text-[10px] text-[#8A8780]">Dispatched immediately</div>
          </button>
          <button onClick={() => setReqType("scheduled")}
                   className={`p-3 rounded-lg border text-left ${reqType === "scheduled" ? "border-[#0B2B66] bg-[#F0F3FA]" : "border-[#E5E2D9]"}`}>
            <Calendar size={14} weight="duotone" color="#0B2B66"/>
            <div className="text-sm font-medium mt-1">Scheduled</div>
            <div className="text-[10px] text-[#8A8780]">Pick a date &amp; time</div>
          </button>
        </div>
        <div className="mt-3">
          <label className="text-[10px] uppercase tracking-[0.15em] text-[#8A8780]">Vehicle class</label>
          <div className="mt-1 flex gap-1.5 flex-wrap">
            {(partner.subcategory === "ambulance"
              ? ["BLS", "ALS", "ICU", "private"]
              : []).map(c => (
              <button key={c} onClick={() => setVehClass(c)} data-testid={`veh-${c}`}
                       className={`h-8 px-3 rounded text-[11px] border ${vehClass === c ? "bg-[#DC2626] text-white border-[#DC2626]" : "bg-white border-[#E5E2D9] text-[#5C5A55]"}`}>
                {c}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-[#E5E2D9] p-4 sm:p-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <AddressPicker
            value={{ address: form.pickup_address, latitude: form.pickup_latitude, longitude: form.pickup_longitude }}
            onChange={(patch) => setForm({
              ...form,
              pickup_address: patch.address ?? form.pickup_address,
              pickup_latitude: patch.latitude ?? form.pickup_latitude,
              pickup_longitude: patch.longitude ?? form.pickup_longitude,
            })}
            label="Pickup address"
            testidPrefix="amb-addr"
          />
          <AmbulanceDropPicker
            value={{ address: form.drop_address, latitude: form.drop_latitude, longitude: form.drop_longitude }}
            pickupLat={form.pickup_latitude}
            pickupLng={form.pickup_longitude}
            ambulancePid={pid}
            onChange={(patch) => setForm({
              ...form,
              drop_address: patch.address ?? form.drop_address,
              drop_latitude: patch.latitude ?? form.drop_latitude,
              drop_longitude: patch.longitude ?? form.drop_longitude,
            })}
          />
          {reqType === "scheduled" && (
            <div className="sm:col-span-2">
              <label className="text-[10px] uppercase tracking-[0.15em] text-[#8A8780]">Scheduled at *</label>
              <input type="datetime-local" value={form.scheduled_at} onChange={e => setForm({ ...form, scheduled_at: e.target.value })}
                      className="mt-1 h-9 w-full border border-[#E5E2D9] rounded px-2 text-sm"/>
            </div>
          )}
          <LovedOnePicker
            selectedId={form.loved_one_id}
            testidPrefix="amb-lop"
            onSelect={(pr) => setForm(f => ({
              ...f,
              loved_one_id: pr.loved_one_id,
              patient_name: pr.name || f.patient_name,
              patient_age: pr.age ? String(pr.age) : f.patient_age,
              contact_phone: pr.phone || f.contact_phone,
            }))}
          />
          <Field label="Patient name" v={form.patient_name} onChange={v => setForm({ ...form, patient_name: v })}/>
          <Field label="Patient age" v={form.patient_age} onChange={v => setForm({ ...form, patient_age: v })} type="number"/>
          <Field label="Contact phone" v={form.contact_phone} onChange={v => setForm({ ...form, contact_phone: v })}/>
          <Field label="Notes" v={form.notes} onChange={v => setForm({ ...form, notes: v })} multiline wide/>
        </div>

        {/* Ambulance fare quote — pickup→drop trip breakdown */}
        {form.pickup_latitude != null && form.drop_latitude != null && (
          <AmbulanceFareCard quote={quote} busy={quoteBusy} hasDrop={true}/>
        )}

        {/* Payment method — only meaningful when we have a fare */}
        {quote && quote.total > 0 && (
          <div className="mt-4" data-testid="amb-pay-method">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#8A8780] font-semibold mb-2">
              Payment method
            </div>
            <div className="grid grid-cols-3 gap-2">
              <button type="button" onClick={() => setPayMethod("wallet")} data-testid="amb-pay-wallet"
                       className={`p-2.5 rounded-lg border text-left text-xs ${payMethod === "wallet" ? "border-[#7A2E18] bg-[#FCEEEA]" : "border-[#E5E2D9] bg-white"}`}>
                <div className="font-medium">Wallet</div>
                <div className="text-[10px] text-[#8A8780]">Rescigo balance</div>
              </button>
              <button type="button" onClick={() => setPayMethod("razorpay")} data-testid="amb-pay-razorpay"
                       className={`p-2.5 rounded-lg border text-left text-xs ${payMethod === "razorpay" ? "border-[#7A2E18] bg-[#FCEEEA]" : "border-[#E5E2D9] bg-white"}`}>
                <div className="font-medium">Razorpay</div>
                <div className="text-[10px] text-[#8A8780]">UPI · Card · Netbanking</div>
              </button>
              <button type="button" onClick={() => setPayMethod("cash")} data-testid="amb-pay-cash"
                       className={`p-2.5 rounded-lg border text-left text-xs ${payMethod === "cash" ? "border-[#7A2E18] bg-[#FCEEEA]" : "border-[#E5E2D9] bg-white"}`}>
                <div className="font-medium">Pay on arrival</div>
                <div className="text-[10px] text-[#8A8780]">Cash / card to driver</div>
              </button>
            </div>
          </div>
        )}

        <button onClick={book} disabled={busy} data-testid="book-amb"
                 className="mt-4 h-10 px-4 bg-[#7A2E18] hover:bg-[#9A3A20] disabled:opacity-50 text-white rounded text-sm font-medium inline-flex items-center gap-1.5">
          {reqType === "emergency" ? <><Lightning size={12}/> Dispatch now</> : <>Confirm scheduled booking <ArrowRight size={12}/></>}
        </button>
      </div>
    </div>
  );
}

// =====================================================================
// My bookings /me/health/bookings
// =====================================================================
export function MyHealthBookings() {
  const [bookings, setBookings] = useState([]);
  const [type, setType] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await custApi.get("/me/health/bookings", { params: { type: type || undefined } });
      setBookings(r.data.bookings || []);
    } catch (e) { toast.error(formatErr(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, [type]);

  return (
    <div className="space-y-4 px-4 sm:px-6 py-5" data-testid="my-health-bookings">
      <BackHeader to="/me/health" title="My health bookings"/>
      <div className="flex gap-2">
        {[
          ["", "All"], ["doctor", "Doctors"], ["lab", "Lab"], ["ambulance", "Ambulance"],
        ].map(([k, l]) => (
          <button key={k} onClick={() => setType(k)}
                   className={`h-8 px-3 rounded text-[11px] border ${type === k ? "bg-[#DC2626] text-white border-[#DC2626]" : "bg-white border-[#E5E2D9] text-[#5C5A55]"}`}>
            {l}
          </button>
        ))}
      </div>
      {loading && <Skeleton/>}
      <div className="space-y-2">
        {bookings.map(b => <BookingRow key={b.id} b={b} reload={load}/>)}
        {!loading && !bookings.length && (
          <div className="bg-white border border-dashed border-[#E5E2D9] rounded-xl p-10 text-center text-[#8A8780] text-sm">
            No bookings yet
          </div>
        )}
      </div>
    </div>
  );
}

const PAY_PILL = {
  paid: "bg-[#DCFCE7] text-[#15803D]", refunded: "bg-[#E0E7FF] text-[#4338CA]",
  unpaid: "bg-[#FDE2E4] text-[#B42828]",
};
function BookingPayPill({ b }) {
  const st = b.payment_status === "paid" || b.payment_status === "refunded" ? b.payment_status : "unpaid";
  if (!(b.amount > 0)) return null;
  return (
    <span data-testid={`booking-pay-status-${b.no}`}
          className={`inline-block mt-0.5 px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider font-semibold ${PAY_PILL[st]}`}>
      {st}{st === "paid" && b.payment?.source ? ` · ${b.payment.source}` : ""}
    </span>
  );
}

function BookingPayNow({ b, reload }) {
  const [busy, setBusy] = useState(false);
  const unpaid = b.amount > 0 && !["paid", "refunded"].includes(b.payment_status)
    && !["cancelled", "no_show"].includes(b.status);
  if (!unpaid) return null;
  const pay = async (method) => {
    setBusy(true);
    if (await payHealthBooking(b, method)) reload();
    setBusy(false);
  };
  return (
    <div className="px-3 pb-3 -mt-1 flex items-center gap-2 flex-wrap" data-testid={`booking-paynow-${b.no}`}>
      <span className="text-[11px] text-[#B42828] font-medium">Payment pending · Pay now:</span>
      <button disabled={busy} onClick={() => pay("wallet")} data-testid={`booking-pay-wallet-${b.no}`}
              className="h-7 px-2.5 text-[11px] rounded bg-[#DC2626] hover:bg-[#B91C1C] text-white disabled:opacity-50">Wallet</button>
      <button disabled={busy} onClick={() => pay("razorpay")} data-testid={`booking-pay-razorpay-${b.no}`}
              className="h-7 px-2.5 text-[11px] rounded border border-[#DC2626] text-[#DC2626] hover:bg-[#FDECEC] disabled:opacity-50">UPI / Card</button>
    </div>
  );
}

function BookingRow({ b, reload }) {
  const [open, setOpen] = useState(false);
  const sm = STATUS_META[b.status] || STATUS_META.requested;
  const Icon = b.type === "doctor" ? Stethoscope : b.type === "lab" ? TestTube : AmbulanceIcon;
  const subtitle = b.type === "doctor" ? `${b.details?.specialty || ""} · ${b.details?.mode} · ${b.details?.slot_date} ${b.details?.slot_time}` :
    b.type === "lab" ? `${(b.details?.tests || []).length} tests · ${b.details?.visit_type} · ${b.details?.slot_date} ${b.details?.slot_time}` :
    `${b.details?.vehicle_class} · ${b.details?.request_type}`;

  const cancel = async () => {
    if (!window.confirm("Cancel this booking?")) return;
    try {
      const r = await custApi.post(`/me/health/bookings/${b.id}/cancel`);
      toast.success(r.data.refunded ? `Booking cancelled · ₹${r.data.refund_amount} refunded to wallet` : "Booking cancelled");
      reload();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const downloadReport = async (rep) => {
    try {
      const r = await custApi.get(`/me/health/bookings/${b.id}/reports/${rep.id}/file`);
      const link = document.createElement("a");
      const c = r.data.content_base64;
      link.href = c.startsWith("data:") ? c : `data:application/pdf;base64,${c}`;
      link.download = r.data.file_name || "report.pdf";
      link.click();
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="bg-white border border-[#E5E2D9] rounded-xl">
      <button onClick={() => setOpen(!open)} className="w-full text-left p-3 flex items-start gap-3" data-testid={`booking-${b.no}`}>
        <div className="w-10 h-10 rounded-full bg-[#F7F6F2] grid place-items-center text-[#DC2626] flex-none">
          <Icon size={16} weight="duotone"/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-mono text-[11px] text-[#8A8780]">{b.no}</span>
            <span className="text-[10px] uppercase tracking-[0.15em]" style={{ color: sm.color }}>
              <sm.Icon size={9} className="inline mr-0.5"/> {sm.lbl}
            </span>
          </div>
          <div className="font-medium text-sm truncate mt-0.5">{b.partner_name}</div>
          <div className="text-[11px] text-[#5C5A55] truncate">{subtitle}</div>
        </div>
        <div className="text-right flex-none">
          <div className="text-sm font-mono">₹{Number(b.amount || 0).toLocaleString("en-IN")}</div>
          <BookingPayPill b={b}/>
        </div>
      </button>
      <BookingPayNow b={b} reload={reload}/>
      {open && (
        <div className="border-t border-[#F0ECE3] p-3 text-xs space-y-2">
          <div className="grid grid-cols-2 gap-2">
            {Object.entries(b.details || {}).map(([k, v]) => {
              if (k === "tests" && Array.isArray(v)) {
                return (
                  <div key={k} className="col-span-2">
                    <div className="text-[10px] uppercase tracking-[0.15em] text-[#8A8780]">Tests</div>
                    <ul className="mt-1">
                      {v.map((t, i) => <li key={i}>· {t.name} (₹{t.price})</li>)}
                    </ul>
                  </div>
                );
              }
              if (k === "vehicle" && v) {
                return (
                  <div key={k} className="col-span-2 text-[11px]">
                    <span className="text-[#8A8780]">Vehicle: </span>
                    <b>{v.registration_no}</b> · driver {v.driver_name || "—"} ({v.driver_phone || "—"})
                  </div>
                );
              }
              if (typeof v === "object" || v == null) return null;
              return (
                <div key={k}>
                  <span className="text-[#8A8780]">{prettyKey(k)}: </span>
                  <span>{String(v)}</span>
                </div>
              );
            })}
          </div>
          {(b.reports || []).length > 0 && (
            <div className="pt-2 border-t border-[#F0ECE3]">
              <div className="text-[10px] uppercase tracking-[0.15em] text-[#8A8780] mb-1.5">Lab reports</div>
              <ul className="space-y-1.5">
                {b.reports.map(rep => (
                  <li key={rep.id} className="flex items-center gap-2">
                    <FileText size={11} color="#DC2626"/>
                    <span>{rep.file_name}</span>
                    <button onClick={() => downloadReport(rep)} data-testid={`download-${rep.id}`}
                             className="ml-auto h-7 px-2 text-[11px] bg-[#DC2626] text-white rounded inline-flex items-center gap-1">
                      <Download size={10}/> Download
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {!["completed", "cancelled", "no_show"].includes(b.status) && (
            <button onClick={cancel} className="mt-2 h-7 px-2 text-[11px] border border-[#F0ECE3] rounded text-[#7A2E18]">
              Cancel booking
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// =====================================================================
// Primitives
// =====================================================================
function BackHeader({ to, title }) {
  return (
    <div className="flex items-center gap-3">
      <Link to={to} className="inline-flex items-center gap-1.5 text-xs text-[#5C5A55] hover:text-[#DC2626]">
        <ArrowLeft size={11}/> Back
      </Link>
      <h1 className="font-heading text-xl flex-1 truncate">{title}</h1>
    </div>
  );
}

function Field({ label, v, onChange, type = "text", placeholder = "", multiline = false, wide = false, testid }) {
  return (
    <div className={wide ? "sm:col-span-2" : ""}>
      <label className="text-[10px] uppercase tracking-[0.15em] text-[#8A8780]">{label}</label>
      {multiline ? (
        <textarea value={v ?? ""} onChange={e => onChange(e.target.value)} rows={3}
                   placeholder={placeholder} data-testid={testid}
                   className="mt-1 w-full border border-[#E5E2D9] rounded-md px-3 py-2 text-sm"/>
      ) : (
        <input value={v ?? ""} onChange={e => onChange(e.target.value)} type={type}
                placeholder={placeholder} data-testid={testid}
                className="mt-1 h-10 w-full border border-[#E5E2D9] rounded-md px-3 text-sm"/>
      )}
    </div>
  );
}

/* ── Ambulance drop-location picker with nearby-hospital + attached-hospital suggestions ── */
function AmbulanceDropPicker({ value, onChange, pickupLat, pickupLng, ambulancePid }) {
  const [nearby, setNearby] = useState([]);
  const [attached, setAttached] = useState([]);

  // Attached hospitals from the ambulance partner config
  useEffect(() => {
    if (!ambulancePid) { setAttached([]); return; }
    custApi.get(`/me/ambulance/${ambulancePid}/hospitals`)
      .then(r => setAttached(r.data.hospitals || []))
      .catch(() => setAttached([]));
  }, [ambulancePid]);

  // Nearby (via geo)
  useEffect(() => {
    if (pickupLat == null || pickupLng == null) { setNearby([]); return; }
    custApi.get("/me/hospitals/nearby", {
      params: { lat: pickupLat, lng: pickupLng, radius_km: 25 },
    })
      .then(r => setNearby((r.data.hospitals || []).map(h => ({
        name: `${h.name}${h.distance_km ? ` · ${h.distance_km} km${h.duration_min ? ` · ${Math.round(h.duration_min)} min` : ""}` : ""}`,
        lat: h.lat, lng: h.lng, address: h.address,
      }))))
      .catch(() => setNearby([]));
  }, [pickupLat, pickupLng]);

  // Combine: attached first, then nearby (avoiding overlaps by name)
  const combined = [
    ...attached.filter(h => h.lat != null && h.lng != null).map(h => ({
      name: h.name, lat: h.lat, lng: h.lng, address: h.address, tag: "attached",
    })),
    ...nearby.filter(n => !attached.some(a => a.name && n.name.startsWith(a.name))),
  ];

  return (
    <div className="sm:col-span-2">
      <AddressPicker
        value={value}
        onChange={onChange}
        nearby={combined}
        label="Drop address"
        testidPrefix="amb-drop-addr"
      />
      {attached.length > 0 && !value?.latitude && (
        <div className="mt-2" data-testid="amb-drop-attached-chips">
          <div className="text-[10px] uppercase tracking-widest text-[#DC2626] font-bold mb-1.5">
            Attached hospitals · one-tap
          </div>
          <div className="flex flex-wrap gap-1.5">
            {attached.slice(0, 8).map(h => (
              <button key={h.id} type="button"
                      onClick={() => onChange({
                        address: h.address || h.name,
                        latitude: h.lat, longitude: h.lng,
                      })}
                      data-testid={`amb-drop-attached-${h.id}`}
                      className="h-8 px-2.5 text-[11px] font-semibold bg-[#FEF2F2] border border-[#FCA5A5] text-[#DC2626] rounded-full hover:bg-[#FEE2E2] inline-flex items-center gap-1">
                {h.name}
                {h.has_trauma_care && <span className="text-[9px] font-bold px-1 rounded bg-[#DC2626] text-white">TR</span>}
                {h.has_air_ambulance && <span className="text-[9px] font-bold px-1 rounded bg-[#2563EB] text-white">AIR</span>}
              </button>
            ))}
          </div>
        </div>
      )}
      {nearby.length > 0 && !value?.latitude && (
        <div className="mt-2" data-testid="amb-drop-nearby-chips">
          <div className="text-[10px] uppercase tracking-widest text-[#5C5A55] font-bold mb-1.5">
            Suggested hospitals nearby
          </div>
          <div className="flex flex-wrap gap-1.5">
            {nearby.slice(0, 6).map((h, i) => (
              <button key={i} type="button"
                      onClick={() => onChange({ address: h.address || h.name, latitude: h.lat, longitude: h.lng })}
                      data-testid={`amb-drop-nearby-${i}`}
                      className="h-8 px-2.5 text-[11px] font-semibold bg-[#EFF6FF] border border-[#DBEAFE] text-[#1D4ED8] rounded-full hover:bg-[#DBEAFE]">
                {h.name}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Skeleton() {
  return (
    <div className="space-y-3">
      {[1, 2, 3].map(i => (
        <div key={i} className="h-16 bg-white border border-[#E5E2D9] rounded-xl animate-pulse"/>
      ))}
    </div>
  );
}

/* ── Ambulance fare card — trip breakdown (pickup→drop) + dead-head ── */
function AmbulanceFareCard({ quote, busy, hasDrop }) {
  if (busy && !quote) {
    return (
      <div className="mt-4 p-4 bg-[#F7F5EE] border border-[#E5E2D9] rounded-lg animate-pulse text-xs text-[#8A8780]"
            data-testid="amb-fare-card-loading">
        Computing road distance…
      </div>
    );
  }
  if (!quote) return null;
  const noTrip = quote.trip_km == null;
  return (
    <div className="mt-4 p-4 bg-gradient-to-br from-[#FCEEEA] to-[#F7F5EE] border border-[#E5B8AA] rounded-lg space-y-2.5"
          data-testid="amb-fare-card">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#7A2E18] font-bold">
          Fare estimate · {quote.vehicle_class}
        </div>
        <span className="text-[9px] uppercase tracking-widest font-bold text-[#8A8780]">
          {quote.source === "osrm" ? "Real road" : "Approx."}
        </span>
      </div>

      {quote.dead_head_km != null && (
        <div className="flex items-center justify-between text-xs text-[#5C5A55]"
              data-testid="amb-fare-dead-head">
          <div>Dispatch dead-head (partner → pickup)</div>
          <div className="font-mono">
            {quote.dead_head_km} km{quote.dead_head_min != null ? ` · ~${Math.round(quote.dead_head_min)} min` : ""}
          </div>
        </div>
      )}

      {noTrip ? (
        <div className="text-xs text-[#8A8780] italic" data-testid="amb-fare-no-drop">
          Set drop location to compute trip distance &amp; fare.
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between text-sm font-medium"
                data-testid="amb-fare-trip">
            <div>
              Trip distance
              <span className="text-[10px] text-[#8A8780] ml-1.5">
                (pickup → drop · ~{Math.round(quote.trip_min || 0)} min)
              </span>
            </div>
            <div className="font-mono text-[#7A2E18]">{quote.trip_km} km</div>
          </div>
          <div className="border-t border-[#E5B8AA] pt-2 space-y-1">
            <div className="flex justify-between text-xs text-[#5C5A55]" data-testid="amb-fare-base">
              <span>Base fare</span>
              <span className="font-mono">₹{quote.base_fare.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-xs text-[#5C5A55]" data-testid="amb-fare-distance">
              <span>Distance charge · @ ₹{quote.per_km_rate}/km × {quote.trip_km} km</span>
              <span className="font-mono">₹{quote.distance_charge.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm font-bold text-[#1A1A1A] pt-1"
                  data-testid="amb-fare-total">
              <span>Total</span>
              <span className="font-mono text-[#7A2E18]">₹{quote.total.toFixed(2)}</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function prettyDate(iso) {
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}
function prettyKey(k) {
  return k.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
}

// =====================================================================
// Iter 154 — Vaccine reminders /me/health/vaccines
// =====================================================================
export function VaccineReminders() {
  const [lovedOnes, setLovedOnes] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ vaccine_name: "", due_date: todayISO(), notes: "" });
  const [view, setView] = useState("list"); // "list" | "calendar"

  const loadLO = async () => {
    try {
      const r = await custApi.get("/me/loved-ones");
      const list = r.data.loved_ones || [];
      setLovedOnes(list);
      if (list.length && !selectedId) setSelectedId(list[0].id);
    } catch (e) { toast.error(formatErr(e)); }
  };
  const loadReminders = async () => {
    if (!selectedId) { setItems([]); setLoading(false); return; }
    setLoading(true);
    try {
      const r = await custApi.get("/me/vaccine-reminders", { params: { loved_one_id: selectedId } });
      setItems(r.data.reminders || []);
    } catch (e) { toast.error(formatErr(e)); }
    setLoading(false);
  };
  useEffect(() => { loadLO(); }, []);
  useEffect(() => { loadReminders(); /* eslint-disable-line */ }, [selectedId]);

  const add = async () => {
    if (!selectedId) return toast.error("Add a loved one on your profile first");
    if (!form.vaccine_name.trim()) return toast.error("Vaccine name required");
    if (!form.due_date) return toast.error("Due date required");
    try {
      await custApi.post("/me/vaccine-reminders", {
        loved_one_id: selectedId,
        vaccine_name: form.vaccine_name.trim(),
        due_date: form.due_date,
        notes: form.notes.trim() || null,
      });
      toast.success("Reminder added");
      setForm({ vaccine_name: "", due_date: todayISO(), notes: "" });
      setShowForm(false);
      loadReminders();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const toggleGiven = async (row) => {
    try {
      await custApi.put(`/me/vaccine-reminders/${row.id}`, { given: !row.given });
      loadReminders();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Remove this vaccine reminder?")) return;
    try {
      await custApi.delete(`/me/vaccine-reminders/${id}`);
      loadReminders();
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="space-y-4 px-4 sm:px-6 py-5" data-testid="me-vaccines">
      <BackHeader to="/me/health" title="Vaccine reminders"/>
      <LovedOneChipRow items={lovedOnes} value={selectedId} onChange={setSelectedId}/>

      {selectedId && (
        <>
          <div className="flex items-center justify-between">
            <div className="text-[11px] uppercase tracking-widest text-[#5C5A55] font-semibold">
              Upcoming vaccines
            </div>
            <div className="flex items-center gap-2">
              {/* View toggle: list ↔ calendar */}
              <div className="inline-flex rounded-full bg-[#F1F5F9] p-1 h-8" data-testid="vac-view-toggle">
                <button onClick={() => setView("list")}
                        data-testid="vac-view-list"
                        className={`h-6 px-3 text-[10px] uppercase tracking-widest font-bold rounded-full transition-colors ${
                          view === "list" ? "bg-white text-[#DC2626] shadow" : "text-[#5C5A55]"
                        }`}>
                  List
                </button>
                <button onClick={() => setView("calendar")}
                        data-testid="vac-view-calendar"
                        className={`h-6 px-3 text-[10px] uppercase tracking-widest font-bold rounded-full transition-colors ${
                          view === "calendar" ? "bg-white text-[#DC2626] shadow" : "text-[#5C5A55]"
                        }`}>
                  Calendar
                </button>
              </div>
              {!showForm && (
                <button onClick={() => setShowForm(true)} data-testid="vac-add"
                        className="h-9 px-3 inline-flex items-center gap-1.5 bg-[#DC2626] hover:bg-[#B91C1C] text-white rounded-md text-xs font-medium">
                  <Plus size={12} weight="bold"/> Add
                </button>
              )}
            </div>
          </div>

          {showForm && (
            <div className="border-2 border-dashed border-[#DC2626] rounded-md p-3 bg-[#FEF7F6] space-y-2"
                 data-testid="vac-form">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#5C5A55] block mb-1">Vaccine name *</label>
                <input value={form.vaccine_name}
                       onChange={e => setForm(s => ({ ...s, vaccine_name: e.target.value }))}
                       data-testid="vac-name"
                       placeholder="e.g. BCG, MMR, DPT booster"
                       className="w-full h-9 px-2 border border-[#E5E2D9] rounded text-sm"/>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#5C5A55] block mb-1">Due date *</label>
                <input type="date" value={form.due_date}
                       onChange={e => setForm(s => ({ ...s, due_date: e.target.value }))}
                       data-testid="vac-date"
                       className="w-full h-9 px-2 border border-[#E5E2D9] rounded text-sm"/>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#5C5A55] block mb-1">Notes</label>
                <input value={form.notes}
                       onChange={e => setForm(s => ({ ...s, notes: e.target.value }))}
                       data-testid="vac-notes"
                       placeholder="Clinic, doctor, etc."
                       className="w-full h-9 px-2 border border-[#E5E2D9] rounded text-sm"/>
              </div>
              <div className="grid grid-cols-2 gap-2 pt-1">
                <button onClick={add} data-testid="vac-save"
                        className="h-9 w-full bg-[#DC2626] hover:bg-[#B91C1C] text-white rounded-md text-sm font-medium">
                  Save reminder
                </button>
                <button onClick={() => setShowForm(false)}
                        className="h-9 w-full border border-[#E5E2D9] rounded-md text-sm">
                  Cancel
                </button>
              </div>
            </div>
          )}

          {loading ? (
            <div className="text-xs text-[#8A8780] text-center py-6">Loading…</div>
          ) : items.length === 0 ? (
            <EmptyReminders Icon={Syringe} text="No vaccines scheduled yet. Tap Add to create one."/>
          ) : view === "calendar" ? (
            <VaccineCalendar items={items} onToggle={toggleGiven}/>
          ) : (
            <div className="space-y-2" data-testid="vac-list">
              {items.map(row => (
                <VaccineRow key={row.id} row={row}
                            onToggle={() => toggleGiven(row)}
                            onDelete={() => remove(row.id)}/>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ────────────────── Vaccine calendar view ──────────────────
function VaccineCalendar({ items, onToggle }) {
  const [monthOffset, setMonthOffset] = useState(0);
  const [picked, setPicked] = useState(null);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const first = new Date(today.getFullYear(), today.getMonth() + monthOffset, 1);
  const daysInMonth = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();
  const startDow = first.getDay();
  const monthLabel = first.toLocaleDateString(undefined, { month: "long", year: "numeric" });

  // Map YYYY-MM-DD -> [rows]
  const byDate = {};
  for (const it of items) {
    (byDate[it.due_date] = byDate[it.due_date] || []).push(it);
  }

  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    const iso = `${first.getFullYear()}-${String(first.getMonth() + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    cells.push({ d, iso, rows: byDate[iso] || [] });
  }

  const todayISOStr = todayISO();
  const pickedRows = picked ? (byDate[picked] || []) : [];

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-[0_4px_20px_rgba(0,0,0,0.03)] p-4"
         data-testid="vac-calendar">
      <div className="flex items-center justify-between mb-3">
        <button onClick={() => setMonthOffset(m => m - 1)}
                data-testid="vac-cal-prev"
                className="w-8 h-8 rounded-full bg-[#F3F4F6] hover:bg-[#E5E7EB] inline-flex items-center justify-center">
          <ArrowLeft size={14}/>
        </button>
        <div className="text-center">
          <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Vaccine calendar</div>
          <div className="font-display font-black text-sm text-[#1A1A1A]">{monthLabel}</div>
        </div>
        <button onClick={() => setMonthOffset(m => m + 1)}
                data-testid="vac-cal-next"
                className="w-8 h-8 rounded-full bg-[#F3F4F6] hover:bg-[#E5E7EB] inline-flex items-center justify-center">
          <ArrowRight size={14}/>
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1 mb-1">
        {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
          <div key={i} className="text-center text-[9px] uppercase tracking-widest text-gray-400 font-bold">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((c, i) => {
          if (!c) return <div key={i} className="h-10"/>;
          const isToday = c.iso === todayISOStr;
          const hasVac = c.rows.length > 0;
          const allDone = hasVac && c.rows.every(r => r.given);
          const overdue = hasVac && !allDone && c.iso < todayISOStr;
          const isPicked = c.iso === picked;
          const bg = allDone ? "#DCFCE7"
                   : overdue ? "#DC2626"
                   : hasVac ? "#FEE2E2" : "transparent";
          const fg = allDone ? "#15803D"
                   : overdue ? "white"
                   : hasVac ? "#DC2626" : "#374151";
          return (
            <button key={i} type="button"
                    onClick={() => hasVac && setPicked(c.iso)}
                    disabled={!hasVac}
                    data-testid={`vac-cal-day-${c.d}`}
                    className={`h-10 rounded-lg flex flex-col items-center justify-center text-[12px] font-semibold relative transition-colors ${
                      isToday ? "ring-2 ring-offset-1 ring-[#111827]" : ""
                    } ${isPicked ? "outline outline-2 outline-[#DC2626]" : ""} ${
                      hasVac ? "cursor-pointer active:scale-95" : "cursor-default"
                    }`}
                    style={{ backgroundColor: bg, color: fg }}>
              <span>{c.d}</span>
              {hasVac && (
                <span className="absolute bottom-0.5 right-0.5 text-[8px] font-mono"
                      style={{ color: overdue ? "white" : fg, opacity: 0.9 }}>
                  {c.rows.length}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Legend */}
      <div className="mt-3 flex flex-wrap gap-2 text-[10px]">
        <Legend color="#FEE2E2" text="#DC2626" label="Upcoming"/>
        <Legend color="#DCFCE7" text="#15803D" label="Given"/>
        <Legend color="#DC2626" text="white" label="Overdue"/>
      </div>

      {/* Details for picked day */}
      {picked && pickedRows.length > 0 && (
        <div className="mt-4 border-t border-gray-100 pt-3 space-y-2" data-testid="vac-cal-picked">
          <div className="flex items-center justify-between">
            <div className="text-[11px] uppercase tracking-widest text-[#5C5A55] font-semibold">
              {new Date(picked + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" })}
            </div>
            <button onClick={() => setPicked(null)}
                    className="text-[10px] text-gray-500 hover:text-[#DC2626]">Close</button>
          </div>
          {pickedRows.map(row => (
            <div key={row.id}
                 className={`border rounded-md p-2.5 flex items-center gap-3 ${
                   row.given ? "bg-[#EFF3EE] border-[#D4E0D1]" : "bg-white border-[#E5E2D9]"
                 }`}>
              <button onClick={() => onToggle(row)}
                      className={`w-8 h-8 rounded-full inline-flex items-center justify-center shrink-0 border-2 ${
                        row.given ? "bg-[#2D5A27] border-[#2D5A27] text-white"
                                  : "border-[#DC2626] text-[#DC2626]"
                      }`}>
                {row.given ? <CheckCircle size={16} weight="fill"/> : <Syringe size={14}/>}
              </button>
              <div className="flex-1 min-w-0">
                <div className={`font-semibold text-[13px] ${row.given ? "line-through text-[#5C5A55]" : "text-[#1A1A1A]"}`}>
                  {row.vaccine_name}
                </div>
                {row.notes && <div className="text-[10px] text-[#5C5A55] mt-0.5">{row.notes}</div>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Legend({ color, text, label }) {
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded"
          style={{ backgroundColor: "#F9FAFB" }}>
      <span className="w-2.5 h-2.5 rounded-sm inline-block"
            style={{ backgroundColor: color }}/>
      <span style={{ color: text === "white" ? "#374151" : text }} className="font-semibold">{label}</span>
    </span>
  );
}

function VaccineRow({ row, onToggle, onDelete }) {
  const overdue = !row.given && new Date(row.due_date + "T00:00:00") < new Date(new Date().toDateString());
  return (
    <div data-testid={`vac-row-${row.id}`}
         className={`border rounded-md p-3 flex items-start gap-3 transition-colors ${
           row.given ? "bg-[#EFF3EE] border-[#D4E0D1]" :
           overdue ? "bg-[#FEE2E2] border-[#DC2626]" :
                     "border-[#E5E2D9] hover:border-[#DC2626]"
         }`}>
      <button onClick={onToggle}
              data-testid={`vac-toggle-${row.id}`}
              className={`w-8 h-8 rounded-full inline-flex items-center justify-center shrink-0 border-2 ${
                row.given ? "bg-[#2D5A27] border-[#2D5A27] text-white"
                          : "border-[#DC2626] text-[#DC2626]"
              }`}>
        {row.given ? <CheckCircle size={16} weight="fill"/> : <Syringe size={14}/>}
      </button>
      <div className="flex-1 min-w-0">
        <div className={`font-semibold text-sm ${row.given ? "line-through text-[#5C5A55]" : "text-[#1A1A1A]"}`}>
          {row.vaccine_name}
        </div>
        <div className="text-[11px] mt-0.5 inline-flex items-center gap-1">
          <Calendar size={10}/> {prettyDate(row.due_date)}
          {overdue && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-[#DC2626] text-white font-bold">OVERDUE</span>}
        </div>
        {row.notes && <div className="text-[11px] text-[#5C5A55] mt-0.5">{row.notes}</div>}
      </div>
      <button onClick={onDelete} data-testid={`vac-del-${row.id}`}
              className="p-1.5 rounded hover:bg-white shrink-0">
        <Trash size={14} color="#DC2626"/>
      </button>
    </div>
  );
}

// =====================================================================
// Iter 154 — Pill reminders /me/health/pills
// =====================================================================
export function PillReminders() {
  const [lovedOnes, setLovedOnes] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(blankPill());

  function blankPill() {
    return {
      medicine_name: "", dosage: "", times: ["morning"],
      interval_hours: "",
      start_date: todayISO(), end_date: "", notes: "",
    };
  }
  const toggleTime = (t) => setForm(s => ({
    ...s,
    times: s.times.includes(t) ? s.times.filter(x => x !== t) : [...s.times, t],
  }));

  const loadLO = async () => {
    try {
      const r = await custApi.get("/me/loved-ones");
      const list = r.data.loved_ones || [];
      setLovedOnes(list);
      if (list.length && !selectedId) setSelectedId(list[0].id);
    } catch (e) { toast.error(formatErr(e)); }
  };
  const loadPills = async () => {
    if (!selectedId) { setItems([]); setLoading(false); return; }
    setLoading(true);
    try {
      const r = await custApi.get("/me/pill-reminders", { params: { loved_one_id: selectedId } });
      setItems(r.data.reminders || []);
    } catch (e) { toast.error(formatErr(e)); }
    setLoading(false);
  };
  useEffect(() => { loadLO(); }, []);
  useEffect(() => { loadPills(); /* eslint-disable-line */ }, [selectedId]);

  const add = async () => {
    if (!selectedId) return toast.error("Add a loved one on your profile first");
    if (!form.medicine_name.trim()) return toast.error("Medicine name required");
    if (form.times.length === 0 && !form.interval_hours) return toast.error("Pick a time or interval");
    try {
      await custApi.post("/me/pill-reminders", {
        loved_one_id: selectedId,
        medicine_name: form.medicine_name.trim(),
        dosage: form.dosage.trim() || null,
        times_of_day: form.times,
        interval_hours: form.interval_hours === "" ? null : Number(form.interval_hours),
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        notes: form.notes.trim() || null,
      });
      toast.success("Reminder added");
      setForm(blankPill());
      setShowForm(false);
      loadPills();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const toggleActive = async (row) => {
    try {
      await custApi.put(`/me/pill-reminders/${row.id}`, { active: !row.active });
      loadPills();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Remove this pill reminder?")) return;
    try {
      await custApi.delete(`/me/pill-reminders/${id}`);
      loadPills();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const TIMES = ["morning", "afternoon", "evening", "night"];

  return (
    <div className="space-y-4 px-4 sm:px-6 py-5" data-testid="me-pills">
      <BackHeader to="/me/health" title="Pill reminders"/>
      <button
        type="button"
        onClick={() => window.dispatchEvent(new CustomEvent("rigo:test-alarm", {
          detail: {
            loved_one_id: selectedId || null,
            medicine_name: (items?.[0]?.medicine_name) || "Metformin 500mg",
            dosage: (items?.[0]?.dosage) || "1 tablet after food",
          },
        }))}
        data-testid="me-pills-test-alarm"
        className="w-full inline-flex items-center justify-center gap-2 h-11 rounded-2xl bg-gradient-to-r from-[#DC2626] to-[#B91C1C] text-white text-sm font-bold shadow-lg shadow-red-500/20 active:scale-[0.99]"
      >
        <BellRinging size={16} weight="fill"/> Test Rigo alarm popup
      </button>
      <AlarmSoundPicker
        category="medicine"
        label={selectedId ? `Alarm sound for ${lovedOnes.find(l => l.id === selectedId)?.name || "this profile"}` : "Medicine alarm sound"}
        lovedOneId={selectedId || null}
      />
      <LovedOneChipRow items={lovedOnes} value={selectedId} onChange={setSelectedId}/>

      {selectedId && (
        <>
          <div className="flex items-center justify-between">
            <div className="text-[11px] uppercase tracking-widest text-[#5C5A55] font-semibold">
              Daily medicines
            </div>
            {!showForm && (
              <button onClick={() => setShowForm(true)} data-testid="pill-add"
                      className="h-9 px-3 inline-flex items-center gap-1.5 bg-[#DC2626] hover:bg-[#B91C1C] text-white rounded-md text-xs font-medium">
                <Plus size={12} weight="bold"/> Add
              </button>
            )}
          </div>

          {showForm && (
            <div className="border-2 border-dashed border-[#DC2626] rounded-md p-3 bg-[#FEF7F6] space-y-2"
                 data-testid="pill-form">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-[#5C5A55] block mb-1">Medicine *</label>
                  <input value={form.medicine_name}
                         onChange={e => setForm(s => ({ ...s, medicine_name: e.target.value }))}
                         data-testid="pill-name"
                         placeholder="e.g. Metformin"
                         className="w-full h-9 px-2 border border-[#E5E2D9] rounded text-sm"/>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-[#5C5A55] block mb-1">Dosage</label>
                  <input value={form.dosage}
                         onChange={e => setForm(s => ({ ...s, dosage: e.target.value }))}
                         data-testid="pill-dosage"
                         placeholder="e.g. 500mg · 1 tab"
                         className="w-full h-9 px-2 border border-[#E5E2D9] rounded text-sm"/>
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#5C5A55] block mb-1">Times of day</label>
                <div className="grid grid-cols-4 gap-1.5">
                  {TIMES.map(t => {
                    const on = form.times.includes(t);
                    return (
                      <button key={t} type="button"
                              data-testid={`pill-time-${t}`}
                              onClick={() => toggleTime(t)}
                              className={`h-9 rounded-md text-xs font-medium border transition-colors ${
                                on
                                  ? "bg-[#DC2626] text-white border-[#DC2626]"
                                  : "bg-white text-[#1A1A1A] border-[#E5E2D9]"
                              }`}>
                        {t}
                      </button>
                    );
                  })}
                </div>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-[#5C5A55] block mb-1">Or specific time</label>
                    <input type="time" data-testid="pill-time-specific"
                           onChange={e => {
                             const v = e.target.value;
                             if (v && !form.times.includes(v)) {
                               setForm(s => ({ ...s, times: [...s.times, v] }));
                             }
                           }}
                           className="w-full h-9 px-2 border border-[#E5E2D9] rounded text-sm font-mono"/>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-[#5C5A55] block mb-1">Every N hours</label>
                    <input type="number" min="1" max="24" value={form.interval_hours}
                           onChange={e => setForm(s => ({ ...s, interval_hours: e.target.value }))}
                           data-testid="pill-interval"
                           placeholder="e.g. 6"
                           className="w-full h-9 px-2 border border-[#E5E2D9] rounded text-sm"/>
                  </div>
                </div>
                {form.times.length > 0 && (
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {form.times.map(t => (
                      <span key={t} className="text-[10px] px-2 py-0.5 rounded-full bg-[#FEE2E2] text-[#DC2626] font-semibold inline-flex items-center gap-1">
                        {t}
                        <button type="button"
                                onClick={() => setForm(s => ({ ...s, times: s.times.filter(x => x !== t) }))}
                                className="text-[#7F1D1D] font-bold">✕</button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-[#5C5A55] block mb-1">Start</label>
                  <input type="date" value={form.start_date}
                         onChange={e => setForm(s => ({ ...s, start_date: e.target.value }))}
                         data-testid="pill-start"
                         className="w-full h-9 px-2 border border-[#E5E2D9] rounded text-sm"/>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-[#5C5A55] block mb-1">End (opt)</label>
                  <input type="date" value={form.end_date}
                         onChange={e => setForm(s => ({ ...s, end_date: e.target.value }))}
                         data-testid="pill-end"
                         className="w-full h-9 px-2 border border-[#E5E2D9] rounded text-sm"/>
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#5C5A55] block mb-1">Notes</label>
                <input value={form.notes}
                       onChange={e => setForm(s => ({ ...s, notes: e.target.value }))}
                       data-testid="pill-notes"
                       placeholder="After food, empty stomach, etc."
                       className="w-full h-9 px-2 border border-[#E5E2D9] rounded text-sm"/>
              </div>
              <div className="grid grid-cols-2 gap-2 pt-1">
                <button onClick={add} data-testid="pill-save"
                        className="h-9 w-full bg-[#DC2626] hover:bg-[#B91C1C] text-white rounded-md text-sm font-medium">
                  Save reminder
                </button>
                <button onClick={() => setShowForm(false)}
                        className="h-9 w-full border border-[#E5E2D9] rounded-md text-sm">
                  Cancel
                </button>
              </div>
            </div>
          )}

          {loading ? (
            <div className="text-xs text-[#8A8780] text-center py-6">Loading…</div>
          ) : items.length === 0 ? (
            <EmptyReminders Icon={PillIcon} text="No pills scheduled yet. Tap Add to create one."/>
          ) : (
            <div className="space-y-2" data-testid="pill-list">
              {items.map(row => (
                <PillRow key={row.id} row={row}
                         onToggle={() => toggleActive(row)}
                         onDelete={() => remove(row.id)}/>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function PillRow({ row, onToggle, onDelete }) {
  return (
    <div data-testid={`pill-row-${row.id}`}
         className={`border rounded-md p-3 flex items-start gap-3 transition-colors ${
           row.active ? "border-[#E5E2D9] hover:border-[#DC2626]"
                      : "bg-[#F7F6F2] border-[#E5E2D9] opacity-60"
         }`}>
      <div className="w-9 h-9 rounded-full bg-[#FEE2E2] text-[#DC2626] inline-flex items-center justify-center shrink-0">
        <PillIcon size={16} weight="fill"/>
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-sm text-[#1A1A1A]">
          {row.medicine_name}
          {row.dosage && <span className="ml-1.5 text-[10px] uppercase tracking-widest text-[#5C5A55] font-normal">{row.dosage}</span>}
        </div>
        {(row.times_of_day || []).length > 0 && (
          <div className="mt-1 flex flex-wrap gap-1">
            {row.times_of_day.map(t => (
              <span key={t} className="text-[10px] px-1.5 py-0.5 rounded bg-[#FEE2E2] text-[#7F1D1D] font-medium capitalize">
                <Bell size={9} className="inline mr-0.5"/> {t}
              </span>
            ))}
            {row.interval_hours && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#DBEAFE] text-[#1E40AF] font-medium">
                every {row.interval_hours}h
              </span>
            )}
          </div>
        )}
        {(row.start_date || row.end_date) && (
          <div className="text-[10px] text-[#8A8780] mt-1">
            {row.start_date ? prettyDate(row.start_date) : "—"} → {row.end_date ? prettyDate(row.end_date) : "ongoing"}
          </div>
        )}
        {row.notes && <div className="text-[11px] text-[#5C5A55] mt-0.5">{row.notes}</div>}
      </div>
      <div className="flex flex-col gap-1 shrink-0">
        <button onClick={onToggle} data-testid={`pill-toggle-${row.id}`}
                className={`h-6 px-2 rounded text-[10px] font-bold ${
                  row.active ? "bg-[#2D5A27] text-white" : "bg-[#E5E2D9] text-[#5C5A55]"
                }`}>
          {row.active ? "ON" : "OFF"}
        </button>
        <button onClick={onDelete} data-testid={`pill-del-${row.id}`}
                className="p-1.5 rounded hover:bg-[#FEE2E2]">
          <Trash size={13} color="#DC2626"/>
        </button>
      </div>
    </div>
  );
}

// Shared bits ----------------------------------------------------------
function LovedOneChipRow({ items, value, onChange }) {
  if (items.length === 0) {
    return (
      <div className="border border-dashed border-[#DC2626] bg-[#FEF7F6] rounded-md p-4 text-center"
           data-testid="lo-empty">
        <Users size={22} color="#DC2626" className="mx-auto mb-1"/>
        <div className="text-sm font-semibold text-[#1A1A1A]">Add a loved one first</div>
        <div className="text-[11px] text-[#5C5A55] mt-1">
          Head to Profile → Health Profile to add family members before setting reminders.
        </div>
        <Link to="/me/profile" data-testid="lo-goto-profile"
              className="mt-3 inline-block h-9 px-4 leading-9 bg-[#DC2626] hover:bg-[#B91C1C] text-white rounded-md text-xs font-medium">
          Go to Profile
        </Link>
      </div>
    );
  }
  return (
    <div data-testid="lo-picker">
      <label className="text-[10px] uppercase tracking-widest text-[#5C5A55] block mb-1.5 font-semibold">
        For whom?
      </label>
      <div className="flex gap-2 overflow-x-auto pb-1">
        {items.map(lo => {
          const on = lo.id === value;
          return (
            <button key={lo.id}
                    data-testid={`lo-chip-${lo.id}`}
                    onClick={() => onChange(lo.id)}
                    className={`h-10 px-3 shrink-0 rounded-full border text-xs font-medium inline-flex items-center gap-1.5 transition-colors ${
                      on
                        ? "bg-[#DC2626] text-white border-[#DC2626]"
                        : "bg-white text-[#1A1A1A] border-[#E5E2D9]"
                    }`}>
              <span className={`w-6 h-6 rounded-full inline-flex items-center justify-center text-[10px] font-bold ${
                on ? "bg-white/25" : "bg-[#FEE2E2] text-[#DC2626]"
              }`}>
                {lo.name?.[0]?.toUpperCase() || "?"}
              </span>
              {lo.name}
              <span className={`text-[9px] uppercase tracking-widest ${on ? "opacity-90" : "opacity-60"}`}>
                {lo.relation}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function EmptyReminders({ Icon, text }) {
  return (
    <div className="border border-dashed border-[#E5E2D9] rounded-md p-6 text-center">
      <Icon size={26} color="#8A8780" className="mx-auto mb-1.5"/>
      <div className="text-xs text-[#5C5A55]">{text}</div>
    </div>
  );
}

function todayISO() {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}
